import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

export type ThemePreference = 'light' | 'dark' | 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly KEY = 'picaggo_theme';

  // Light, not 'system'. Picaggo's design language is a light one — most screens
  // are still built from hardcoded light colours that the dark overrides only
  // partly cover — so following the OS handed a dark-phone user a half-converted
  // app on their very first visit. 'system' stays a valid preference for anyone
  // who sets it deliberately; it is just no longer what you get by default.
  private static readonly DEFAULT_PREFERENCE: ThemePreference = 'light';

  private _pref = new BehaviorSubject<ThemePreference>(ThemeService.DEFAULT_PREFERENCE);
  readonly preference$ = this._pref.asObservable();
  private sysQuery = window.matchMedia('(prefers-color-scheme: dark)');

  init(): void {
    // With the toggle hidden (production), a preference saved back when it was
    // visible would leave that user in dark with no way out — so the switcher
    // being off pins the app to light and ignores whatever is stored. The stored
    // value is left alone, so their choice returns if the toggle ever ships.
    if (!environment.features.themeSwitcher) {
      this._pref.next('light');
      this.apply();
      return;
    }

    const saved = (localStorage.getItem(this.KEY) as ThemePreference)
      || ThemeService.DEFAULT_PREFERENCE;
    this._pref.next(saved);
    this.apply();
    this.sysQuery.addEventListener('change', () => {
      if (this._pref.value === 'system') this.apply();
    });
  }

  set(pref: ThemePreference): void {
    this._pref.next(pref);
    localStorage.setItem(this.KEY, pref);
    this.apply();
  }

  toggle(): void {
    this.set(this.isDark ? 'light' : 'dark');
  }

  get preference(): ThemePreference { return this._pref.value; }

  get isDark(): boolean {
    return this._pref.value === 'dark' ||
      (this._pref.value === 'system' && this.sysQuery.matches);
  }

  /**
   * Re-assert the user's preference on the document.
   *
   * A few screens are light-only by design (login, guest-access,
   * open-event-invite) and pin data-theme to light while they are mounted. They
   * used to put it back by restoring the attribute value they captured on
   * entry, which loses the real preference in two ways: if two such screens
   * overlap, the second captures the first's forced "light" and restores that;
   * and if a destroy hook does not run, the forced value simply persists —
   * leaving the app light while the switch still reads dark.
   *
   * Calling this instead is idempotent and order-independent: it restores what
   * the user actually chose, no matter what the attribute happens to say.
   */
  reapply(): void {
    this.apply();
  }

  private apply(): void {
    document.documentElement.setAttribute('data-theme', this.isDark ? 'dark' : 'light');
  }
}
